<script>
    import { onMount } from "svelte";

    import { apiMethods } from "../../Services/index.svelte";

    import {
        getValidBillingData,
        goToURL,
        historyBack,
        isObjectEmpty,
        replaceURL,
        resetPreventRedirectProperty,
        showException,
        showError,
        getInsuranceType,
        getFormatDate,
        getEncounterStatus,
    } from "../../Utils/utilFunctions.svelte";

    import {
        asaItemsLogic,
        mbsItemsLogic,
        mbsToAsaDisallowAsaByInsuranceType,
        mbsToAsaDisallowInsuranceTypeByState,
        amaUnitValue,
        preventRedirect,
        preventRedirectObj,
        user,
    } from "../../stores";

    import { PageLoading, Tab } from "../../Components/Common/index.svelte";

    import {
    checkPatientPaidPaymentsViaChangeEncounterStatus,
        editIcon,
        headerRightBtnCls,
    } from "../../Utils/utilConstants.svelte";

    import GeneralInfoTab from "./tabs/GeneralInfoTab/GeneralInfoTab.svelte";
    import QuoteTab from "./tabs/QuoteTab/QuoteTab.svelte";
    import CommunicationTab from "./tabs/CommunicationTab/CommunicationTab.svelte";
    import NotesTab from "./tabs/NotesTab/NotesTab.svelte";
    import { getInsuranceFundById } from "../../Utils/utilCache.svelte";
    import ViewEncounterHeader from "../PatientEncounter/components/ViewEncounterHeader.svelte";
    import CommunicationToggle from "../../Components/Common/CommunicationToggle.svelte";

    import { isMobile } from "../../stores";
    import OcrStickerModal from "../../Components/Common/OcrStickerModal.svelte";
    import { DirectEncounterStatus, DirectUserRoles, encounterStatus } from "../../Utils/utilEnums.svelte";
    import Loader from "../../Components/Common/Loader.svelte";
    import NotificationModal from "../../Components/Common/NotificationModal.svelte";
    import ConvertQuoteOnlyToPatientEncounter from "../PatientEncounter/components/ConvertQuoteOnlyToPatientEncounter.svelte";

    export let params = {};

    let isLoadingPatientEncounter = false,
        patientEncounter = {},
        patientDetails = {},
        procedureDetails = {},
        paymentsData = [],
        doctorDetails = {},
        patientContacts = [],
        doctorProviders = [],
        emailNotifications = [],
        smsNotifications = [];

    let billingDetails = {}, // in this case billing details is latest quote details
        quotesData = [],
        latestQuoteData = {},
        latestQuoteDataItems = [],
        mbsTimeData = [];

    let isUpdatingBillingStatus = false;

    let currentTab = "";

    let isNotesInEdit = false,
        notesRef,
        callbackFunc = () => {};

    let commsLoaded = false,
        isCommsLoading = false;

    let eclipseLogsData = [];

    let convertQuoteToEncounterModalOpen = false;

    $: $preventRedirect = isNotesInEdit;

    $: if ($preventRedirect) updatePreventRedirectObj();
    else resetPreventRedirectProperty();

    onMount(() => {
        if (params.id) loadPatientEncounter(params.id);

        if (!params.query) currentTab = "generalInfo";
        else _syncTab(params.query);
    });

    const updateURL = () => {
        let url = `/PatientQuote/view/${params.id}/`;

        if (currentTab) url += `tab=${currentTab}`;

        replaceURL(url);
    };

    const loadPatientEncounter = async (id) => {
        isLoadingPatientEncounter = true;

        await apiMethods.getMethod
            .getPatientEncounterById(id)
            .then(async (res) => {
                if (res.error)
                    showError(
                        res.error ??
                            "Failed to load this fee estimate, please refresh the page and try again and contact support if the issue persists.",
                        historyBack,
                    );
                else await _handleEncounterResponse(res);
            })
            .catch((err) => showException(err, historyBack));

         // doctor providers in this case shall NEVER be changed while investigating encounter
        await apiMethods.getMethod
            .getAccountProvider(doctorDetails.id)
            .then((res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to load doctor details, please refresh the page and try again and contact support if the issue persists.",
                    );
                else doctorProviders = [...res.results];
            })
            .catch((err) => showException(err));

        isLoadingPatientEncounter = false;
    };

    const reloadPatientEncounter = async (response = {}) => {
        if (response && !isObjectEmpty(response)) {
            patientEncounter = { ...response };
            return;
        }

        await apiMethods.getMethod
            .getPatientEncounterById(params.id, true)
            .then((res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to reload this encounter details, please refresh the page and try again and contact support if the issue persists.",
                    );
                else patientEncounter = { ...res };
            })
            .catch((err) => showError(err));
    };

    const loadItemsLogic = async () => {
        await apiMethods.getMethod
            .getItemsLogic()
            .then((res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to load items logic, please refresh the page and try again and contact support if the issue persists.",
                    );
                else _handleItemsLogic(res);
            })
            .catch((err) => showException(err));
    };

    const reloadPatientDetails = async () => {
        await apiMethods.getMethod
            .getPatientEncounterById(params.id)
            .then(async (res) => {
                patientEncounter = { ...res };

                await _updatePatientDetails();
            });
    };

    const reloadCommunication = () => {
        commsLoaded = false;
        loadCommunicationData();
    };

    const updatePatientEncounter = (billingDataArr = []) => {
        if (!billingDataArr.length) reloadPatientDetails();
        else {
            patientEncounter = {
                ...patientEncounter,
                billingData: [...billingDataArr],
            };

            apiMethods.putMethod
                .updatePatientEncounter(patientEncounter, false, "Update fee estimate")
                .then((res) => {
                    if (res.error) showError(res.error);
                    else patientEncounter = { ...res };
                });
        }
    };

    const reloadBillingDetails = async (
        updateCommunication = false,
        response = {},
    ) => {
        if (!params.id) return;

        await reloadPatientEncounter(response);

        await _updatePatientDetails();

        _updateBillingDetails();

        _updateQuotesData();

        await _syncAsaTimeData();

        if (updateCommunication) loadCommunicationData();
    };

    const editEncounter = () => goToURL(`/PatientQuote/edit/${params.id}`);

    const updatePreventRedirectObj = () => {
        $preventRedirectObj = {
            message:
                "Your changes have not been saved, do you want to save before leaving this page?",
            btnOptions: [
                {
                    class: "btn-error",
                    text: "Discard changes",
                    function: () => discardChangeAndRedirect(callbackFunc),
                },
                {
                    text: "Save and continue",
                    function: () => confirmChangeAndRedirect(callbackFunc),
                },
            ],
        };
    };

    const discardChangeAndRedirect = async (callback) => {
        await notesRef.discardChanges();

        callback();
    };

    const confirmChangeAndRedirect = async (callback) => {
        await notesRef.updateNotes();

        callback();
    };

    // HELPER FUNCTIONS
    const _syncTab = (query) => {
        const arr = query.split("&");

        for (let i = 0; i < arr.length; i++) {
            if (arr[i].includes("tab=")) currentTab = arr[i].split("=")[1];
        }

        updateURL();
    };

    const _handleEncounterResponse = async (res) => {
        patientEncounter = { ...res };

        await _updatePatientDetails();

        if (
            !$mbsItemsLogic.length &&
            !$mbsToAsaDisallowAsaByInsuranceType.length &&
            isObjectEmpty($mbsToAsaDisallowInsuranceTypeByState)
        )
            await loadItemsLogic();

        _updateProcedureDetails();

        _updateDoctorDetails();

        _updateBillingDetails();

        _updateQuotesData();

        await _syncAsaTimeData();
    };

    const _updatePatientDetails = async () => {
        const {
            opvStatusMedicare,
            opvStatusHealthFund,
            opvManuallyApproved,
            opvStatusVeteran,
        } = patientEncounter;

        const latestBillingData = getValidBillingData(
            patientEncounter.billingData,
        );

        patientDetails = {
            ...patientEncounter.patient,
            opvStatusMedicare,
            opvStatusHealthFund,
            opvManuallyApproved,
            opvStatusVeteran,
            patientEncounterId: patientEncounter.id,
        };

        patientContacts = [];

        patientContacts = patientDetails.patientContacts;

        if (!isObjectEmpty(latestBillingData)) {
            const {
                patientInsurance,
                patientInsuranceId,
                patientInsuranceType,
                patientMedicareNo,
                patientMedicareRefNo,
                patientMembershipNo,
                patientMembershipRefNo,
            } = latestBillingData;

            patientDetails = {
                ...patientDetails,
                insurance: patientInsurance.replaceAll("undefined", ""),
                insuranceId: patientInsuranceId,
                insuranceType: patientInsuranceType,
                medicareNo: patientMedicareNo,
                medicareRefNo: patientMedicareRefNo,
                membershipNo: patientMembershipNo,
                membershipRefNo: patientMembershipRefNo,
            };
        }

        if ((patientDetails.insuranceType == 0 || patientDetails.insuranceType == 10) &&
            patientDetails.insuranceId) {
            let res = await getInsuranceFundById(patientDetails.insuranceId);

            patientDetails.insuranceName = res.name ?? "";
        }else if(patientDetails.insuranceType == 10 && patientDetails.insuranceId == 0) {
                //default overseas fund
                patientDetails.insuranceName = "Other Overseas Insurance";
        } 
        else
            patientDetails.insuranceName = getInsuranceType(
                patientDetails.insuranceType,
            );
    };

    const _handleItemsLogic = ({
        AsaLogic,
        MbsLogic,
        MbsToAsa,
        InsuranceRulesLogic,
    }) => {
        _syncItemsLogic("mbs", MbsLogic.ConditionalItemsHide);
        _syncItemsLogic("asa", AsaLogic.ConditionalItemsHide);
        _syncItemsLogic("insuranceType", MbsToAsa.DisallowAsaByInsuranceType);
        _syncItemsLogic(
            "insuranceTypeByState",
            MbsToAsa.DisallowSwitchMbsToAsaInInvoiceIfQuoteIsMbsByInsuranceTypeAndState,
        );
        _syncItemsLogic(
            "insuranceRulesLogic",
            InsuranceRulesLogic.AMAUnitValue,
        );
    };

    const _syncItemsLogic = (type, conditionalObj = {}) => {
        if (type == "insuranceRulesLogic")
            $amaUnitValue = Number(conditionalObj);

        if (isObjectEmpty(conditionalObj)) return;

        if (type == "insuranceType")
            $mbsToAsaDisallowAsaByInsuranceType = [...conditionalObj];
        else {
            const keys = Object.keys(conditionalObj);

            for (const key of keys) {
                switch (type) {
                    case "mbs":
                        $mbsItemsLogic.push({
                            hideItem: key,
                            targetItem: conditionalObj[key],
                        });
                        break;
                    case "asa":
                        $asaItemsLogic.push({
                            hideItem: key,
                            targetItem: conditionalObj[key],
                        });
                        break;
                    case "insuranceTypeByState":
                        $mbsToAsaDisallowInsuranceTypeByState = {
                            ...conditionalObj,
                        };
                        break;
                    default:
                        break;
                }
            }
        }
    };

    const _updateProcedureDetails = () => {
        const {
            procedureName,
            scheduledDate,
            createdDate,
            updatedDate,
            neuraxialType,
            typeOfBill,
            id: encounterId,
        } = patientEncounter;

        procedureDetails = {
            procedureName,
            scheduledDate,
            createdDate,
            updatedDate,
            neuraxialType,
            typeOfBill,
            encounterId,
        };

        if (patientEncounter.hospital?.id) {
            procedureDetails = {
                ...procedureDetails,
                hospital: { ...patientEncounter.hospital },
            };
        } else procedureDetails.hospital = {};

        if (patientEncounter.surgeon?.id) {
            procedureDetails = {
                ...procedureDetails,
                surgeon: { ...patientEncounter.surgeon },
            };
        } else procedureDetails.surgeon = {};
    };

    const _updateDoctorDetails = () => {
        const {
            doctor: {
                firstName,
                lastName,
                fullName,
                title,
                phone,
                id,
                profile,
            },
        } = patientEncounter;

        doctorDetails = {
            firstName,
            lastName,
            fullName,
            title,
            phone,
            id,
            profile,
        };

        if (patientEncounter.doctorProvider) {
            const {
                doctorProvider: {
                    number,
                    location,
                    locationAddress,
                    locationAddress2,
                    city,
                    state,
                    postCode,
                    isNoGapBupa,
                    isNoGapHCF,
                    isNoGapNib,
                    isPreferredDefence,
                },
            } = patientEncounter;

            doctorDetails = {
                ...doctorDetails,
                number,
                location,
                locationAddress,
                locationAddress2,
                city,
                state,
                postCode,
                isNoGapBupa,
                isNoGapHCF,
                isNoGapNib,
                isPreferredDefence,
            };
        }
    };

    const _updateBillingDetails = async () => {
        isUpdatingBillingStatus = true;

        const {
            billingStatus,
            skipQuote,
            eligibilityStatusMedicare,
            eligibilityStatusHealthFund,
            eligibilityManuallyApproved,
            claimStatus,
            claimERAStatus,
            claimTransactionId,
            claimLastTry,
            claimTries,
            patientEncounterWorkCoverData,
            patientEncounterPIPData,
        } = patientEncounter;

        billingDetails = {
            billingStatus,
            defaultBillingStatus: billingStatus,
            skipQuote,
            eligibilityStatusMedicare,
            eligibilityStatusHealthFund,
            eligibilityManuallyApproved,
            claimStatus,
            claimERAStatus,
            claimTransactionId,
            patientEncounterId: patientEncounter.id,
            claimLastTry,
            claimTries,
            patientEncounterWorkCoverData,
            patientEncounterPIPData,
        };

        if (
            patientEncounter.billingData &&
            patientEncounter.billingData.length
        ) {
            billingDetails.billingData = getValidBillingData(
                patientEncounter.billingData,
            );

            billingDetails.billingType =
                getValidBillingData(patientEncounter.billingData).billingType ??
                "";
        } else billingDetails.billingData = {};

        _updateLatestDetails();

        isUpdatingBillingStatus = false;
    };

    const _updateLatestDetails = () => {
        latestQuoteData = patientEncounter.billingData?.length
            ? (patientEncounter.billingData.filter(
                  (bd) => bd.isLatestQuoteData,
              )[0] ?? {})
            : {};

        latestQuoteDataItems = !isObjectEmpty(latestQuoteData)
            ? latestQuoteData.billingCodesType == 0
                ? latestQuoteData.mbsData?.length
                    ? [...latestQuoteData.mbsData]
                    : []
                : latestQuoteData.billingCodesType == 1
                  ? latestQuoteData.asaData?.length
                      ? [...latestQuoteData.asaData]
                      : []
                  : latestQuoteData.billingCodesType == 2
                    ? latestQuoteData.freeTextData?.length
                        ? [...latestQuoteData.freeTextData]
                        : []
                    : []
            : [];
    };

    const _updateQuotesData = () => {
        if (!patientEncounter.estimates.length) quotesData = [];
        else
            quotesData = [
                ...patientEncounter.estimates.filter(
                    (estimate) => estimate.status != Number(9),
                ),
            ];
    };

    const loadCommunicationData = async () => {
        isCommsLoading = true;
        apiMethods.getMethod
            .getEncounterCommunications(params.id)
            .then((res) => {
                isCommsLoading = false;
                if (res.error) showError(res.error);
                else {
                    commsLoaded = true;
                    emailNotifications = res.emailNotifications;
                    smsNotifications = res.smsNotifications;
                }
            })
            .catch((err) => {
                showError(err);
                isCommsLoading = false;
            });
    };

    const _syncAsaTimeData = async () => {
        const latestDataHasAsaTimeData =
            latestQuoteDataItems.length &&
            latestQuoteData.useAsaCodes &&
            latestQuoteData.asaTimeData &&
            latestQuoteData.asaTimeData.length;

        if (latestDataHasAsaTimeData && !mbsTimeData.length)
            await apiMethods.getMethod
                .getMBSItems(0, 999999, { "filter[infoType]": "Time" })
                .then((res) => {
                    if (res.results?.length) mbsTimeData = [...res.results];
                });

        if (latestDataHasAsaTimeData) {
            const targetItem = mbsTimeData.find(
                (td) => td.basicUnits == latestQuoteData.asaTimeData[0].units,
            );

            latestQuoteDataItems = [
                ...latestQuoteDataItems,
                {
                    ...latestQuoteData.asaTimeData[0],
                    amaUnits: latestQuoteData.asaTimeData[0].units,
                    rebate: latestQuoteData.asaTimeData[0].feeCharged,
                    rebateAMA: latestQuoteData.asaTimeData[0].feeCharged,
                    mbsItemNum: targetItem?.itemNum,
                },
            ];
        }
    }; 

    const _checkItemsNotSaved = (tab = "") => {
        if (isNotesInEdit) {
            $preventRedirectObj.showModal = $preventRedirect;

            callbackFunc = () => _handleSwitchTab(tab);
        } else {
            callbackFunc = () => {};
            if (!tab) return;
            _handleSwitchTab(tab);
        }
    };

    const _handleSwitchTab = (tab = "") => {
        if (!tab) return;

        currentTab = tab;

        updateURL();
    };

    $: if (currentTab === "communication" && !commsLoaded)
        loadCommunicationData();

    const duplicateEncounter = () => {
        $preventRedirectObj.showModal = $preventRedirect;

        if (isNotesInEdit)
            callbackFunc = () =>
                goToURL(
                    `/PatientQuote/edit/${patientEncounter.id}/isDuplicate=2`,
                );
        else
            goToURL(
                `/PatientQuote/edit/${patientEncounter.id}/isDuplicate=2`,
            );
    };

    const _convertToEncounter = () => {
        convertQuoteToEncounterModalOpen = true;
    }

    const _closeConvertToEncounter = () => {
        convertQuoteToEncounterModalOpen = false;
    };

    let isOcrStickerModalOpen = false;

    $: encounterDoctorId = patientEncounter.doctor?.id;

    const showOcrStickerModal = () => (isOcrStickerModalOpen = true);

    $: isUserEnableUpdatePatientEncounterStatus =
        $user.role >= 2 && $user.role != DirectUserRoles.Developer;

    let isChangingStatusOptionsOpen = false;
    let isUpdatingEncounterStatus = false;

    const _showStatusMenu = () => {
        if (document.getElementById("encounterStatusDropDownMenu")) {
            document.getElementById(
                "encounterStatusDropDownMenu",
            ).style.display = "flex";
            isChangingStatusOptionsOpen = true;
        }
    };


    const _hideStatusMenu = () => {
        if (document.getElementById("encounterStatusDropDownMenu")) {
            document.getElementById(
                "encounterStatusDropDownMenu",
            ).style.display = "none";
            isChangingStatusOptionsOpen = false;
        }
    };

    const _getStatusBackgroundColor = (status) => {
        if (status == 9) return "bg-error";
        if (status == 0) return "";
        return "bg-warning";
    };

    let isConfirmUpdateEncounterStatusModalOpen = false,
        newStatus = null;

    const _openConfirmUpdateEncounterStatusModal = (status) => {
        _hideStatusMenu();
        newStatus = status;

        _determineArchiveWithActiveClaim();

        _determineCancelWithPatientPaidPayments();

        if (
            encounterHasPatientPaidPayments &&
            checkPatientPaidPaymentsViaChangeEncounterStatus.includes(newStatus)
        )
            isConfirmCancelEncounterWithPatientPaidPaymentsModalOpen = true;
        else isConfirmUpdateEncounterStatusModalOpen = true;
    };

    const _closeConfirmUpdateEncounterStatusModal = () => {
        newStatus = null;
        isConfirmUpdateEncounterStatusModalOpen = false;
        isConfirmCancelEncounterWithPatientPaidPaymentsModalOpen = false;
    };

    const checkClaimStatusViaChangeStatus = [7, 8, 9];

    const _determineArchiveWithActiveClaim = () => {
        const { claimERAStatus, claimStatus, claimTransactionId } =
            patientEncounter;

        encounterHasActiveClaimStatus = ![5, 10].includes(claimStatus);
        encounterHasActiveClaimEraStatus = ![2, 10].includes(claimERAStatus);

        encounterHasActiveClaim = checkClaimStatusViaChangeStatus.includes(
            newStatus,
        )
            ? !!claimTransactionId &&
              (encounterHasActiveClaimStatus ||
                  encounterHasActiveClaimEraStatus)
            : false;
    };

    const _determineCancelWithPatientPaidPayments = () => {
        if (!paymentsData) encounterHasPatientPaidPayments = false;
        else
            encounterHasPatientPaidPayments = paymentsData.some(
                ({ paidBy, status }) => paidBy == 0 && [1, 2].includes(status),
            );
    };

    let encounterHasActiveClaim = false,
        encounterHasActiveClaimStatus = false,
        encounterHasActiveClaimEraStatus = false;
    
     let encounterHasPatientPaidPayments = false,
        isConfirmCancelEncounterWithPatientPaidPaymentsModalOpen = false;

    const handleUpdateStatus = async (addSkipRefundToUrl = false) => {
        if (isUpdatingEncounterStatus) return;

        isUpdatingEncounterStatus = true;

        if (checkClaimStatusViaChangeStatus.includes(newStatus)) {
            if (encounterHasActiveClaim) {
                encounterHasActiveClaimStatus &&
                    (await apiMethods.putMethod.updateClaimStatus(
                        patientEncounter.id,
                        10,
                    ));
                encounterHasActiveClaimEraStatus &&
                    (await apiMethods.putMethod.updateClaimEraStatus(
                        patientEncounter.id,
                        10,
                    ));
            }

            if (
                checkPatientPaidPaymentsViaChangeEncounterStatus.includes(
                    newStatus,
                ) &&
                encounterHasPatientPaidPayments &&
                addSkipRefundToUrl
            )
                await cancelEncounterWithPatientPayment();
            else await updateEncounterStatus(newStatus);
        } else if (newStatus == 0 && patientEncounter.newStatus == 9)
            // restore deleted encounter back to active status
            await confirmUnArchivePatientEncounter();
        else await updateEncounterStatus(newStatus);

        isUpdatingEncounterStatus = false;

        _closeConfirmUpdateEncounterStatusModal();
    };

    const updateEncounterStatus = async (status) => {
        if (status != 9)
            await apiMethods.putMethod
                .updateEncounterStatus(patientEncounter.id, status)
                .then(async (res) => {
                    if (!res || res.error)
                        showError(
                            res.error ??
                                "Failed to update encounter status, please refresh the page and try again and contact support if the issue persists.",
                        );
                    else reloadPatientEncounter();
                });
        else await confirmDeletePatientEncounter();
    };

    const confirmDeletePatientEncounter = async () => {
        await apiMethods.deleteMethod
            .deletePatientEncounter(patientEncounter.id)
            .then(async (res) => {
                if (res.id) await reloadPatientEncounter();
                else if (
                    res.error &&
                    res.error.includes("Encounter has a claim")
                ) {
                    showError(
                        "For purposes of auditing, we are unable to archive encounters with an active claim.",
                        undefined,
                        true,
                    );
                } else {
                    showError(
                        `Failed to archive this patient encounter, please try again later or contact for support...\n${res.error}`,
                        undefined,
                        true,
                    );
                }
            })
            .catch((err) =>
                showException(
                    `Failed to archive this patient encounter, please try again later or contact for support...\n${err}`,
                    undefined,
                    true,
                ),
            );
    };

    const cancelEncounterWithPatientPayment = async () => {
        await apiMethods.putMethod
            .cancelEncounterWithoutRefund(patientEncounter.id)
            .then((res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to cancel encounter, please refresh the page and try again and contact support if the issue persists.",
                    );
                else reloadPatientEncounter();
            });
    };

    const confirmUnArchivePatientEncounter = async () => {
        await apiMethods.putMethod
            .unDeletePatientEncounter(patientEncounter.id)
            .then(async (res) => {
                if (res.id) await reloadPatientEncounter();
                else
                    showError(
                        `Failed to un-archive this patient encounter, please refresh the page and try again and contact support if the issue persists. \n${res.error}`,
                    );
            })
            .catch((err) =>
                showException(
                    `Failed to un-archive this patient encounter, please refresh the page and try again and contact support if the issue persists. \n${err}`,
                ),
            );
    };

</script>

{#if isLoadingPatientEncounter}
    <PageLoading />
{:else}
    <ViewEncounterHeader
        {patientEncounter}
        {patientDetails}
        {procedureDetails}
        {isLoadingPatientEncounter}
        {isNotesInEdit}
        bind:callbackFunc
        reloadPatientEncounter={() => loadPatientEncounter(params.id)}
        isPatientQuote
    />

    <div class="flex flex-1 flex-col shadow-2xl rounded-lg bg-white">
        <div class="tabs m-4 flex-wrap">
            <Tab
                {currentTab}
                displayTab="General Info"
                tabUrl="generalInfo"
                clickTabFunc={() => _checkItemsNotSaved("generalInfo")}
            />

            <Tab
                {currentTab}
                displayTab="Fee Estimate"
                tabUrl="feeEstimate"
                clickTabFunc={() => _checkItemsNotSaved("feeEstimate")}
            />

            <Tab
                {currentTab}
                displayTab="Notes"
                tabUrl="notes"
                clickTabFunc={() => _checkItemsNotSaved("notes")}
            >
                {#if patientEncounter.hasNotes || patientDetails?.hasNotes}
                    <div
                        class="tooltip tooltip-secondary ml-3"
                        data-tip="Notes available"
                    >
                        <i class="fa-solid fa-bookmark" aria-hidden="true" />
                    </div>
                {/if}
            </Tab>

            <Tab
                {currentTab}
                displayTab="Communication"
                tabUrl="communication"
                clickTabFunc={() => _checkItemsNotSaved("communication")}
            >
                {#if patientEncounter.disableNotifications}
                    <div
                        class="tooltip tooltip-error ml-3"
                        data-tip="Communication is disabled for this fee estimate"
                    >
                        <i class="fa fa-exclamation-circle"></i>
                    </div>
                {/if}
            </Tab>

            <div class="flex flex-1 items-center justify-end gap-x-3">
                {#if $isMobile}
                    <CommunicationToggle
                        {patientEncounter}
                        on:disableNotificationsToggled={() =>
                            (patientEncounter.disableNotifications =
                                !patientEncounter.disableNotifications)}
                    />
                    {#if currentTab == "generalInfo"}
                        <button
                            class={`${headerRightBtnCls} w-24`}
                            on:click={editEncounter}
                        >
                            <i class={`${editIcon}`} />
                            Edit
                        </button>
                    {/if}
                {/if}
                {#if !$isMobile}
                       {#if patientEncounter.isDeleted}
                            <p class="text-error font-bold pt-1">
                                Archived on {getFormatDate(patientEncounter.deletedDate)}
                            </p>
                        {/if}

                    {#if !isLoadingPatientEncounter}

                        <button class={headerRightBtnCls} on:click={_convertToEncounter}>
                            <i class="fa fa-clone" />
                            Convert
                        </button>

                        {#if isUserEnableUpdatePatientEncounterStatus}
                            <!-- svelte-ignore a11y-no-static-element-interactions -->
                            <div class="flex-none" on:mouseleave={_hideStatusMenu}>
                                <div class="menu menu-horizontal">
                                    <!-- svelte-ignore a11y-no-noninteractive-tabindex -->
                                    <li tabindex="0" on:mousemove={_showStatusMenu}>
                                        <div
                                            class={`${headerRightBtnCls} ${_getStatusBackgroundColor(patientEncounter.status)} flex items-center ${isChangingStatusOptionsOpen ? "rounded-t-lg" : "rounded-lg"}`}
                                        >
                                            {#if isUpdatingEncounterStatus}
                                                <Loader size="1rem" />
                                            {/if}
                                            <p>
                                                {getEncounterStatus(
                                                    patientEncounter.status,
                                                )}
                                            </p>
                                            <svg
                                                class="fill-current"
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="20"
                                                height="20"
                                                viewBox="0 0 24 24"
                                                ><path
                                                    d="M7.41,8.58L12,13.17L16.59,8.58L18,10L12,16L6,10L7.41,8.58Z"
                                                /></svg
                                            >
                                        </div>

                                        <ul
                                            id="encounterStatusDropDownMenu"
                                            class="z-[1000] dropdown-content menu border-0 bg-zinc-300 w-48 rounded-b-lg overflow-hidden"
                                        >
                                            <div>
                                                {#each encounterStatus as { id, type }}
                                                    <!-- svelte-ignore a11y-click-events-have-key-events -->
                                                    <div
                                                        class={`flex items-center justify-start p-4 gap-x-2 border-0 h-12 cursor-pointer ${id == patientEncounter.status ? "bg-secondary text-white" : "bg-zinc-200 text-[#6f726c] hover:bg-zinc-300"}`}
                                                        on:click|preventDefault={() =>
                                                            _openConfirmUpdateEncounterStatusModal(
                                                                id,
                                                            )}
                                                    >
                                                        {type}
                                                    </div>
                                                {/each}
                                            </div>
                                        </ul>
                                    </li>
                                </div>
                            </div>
                        {/if}

                        <button
                            class={headerRightBtnCls}
                            on:click={showOcrStickerModal}
                        >
                            <i class="fa-solid fa-image" />
                            Sticker
                        </button>

                        <button class={headerRightBtnCls} on:click={duplicateEncounter}>
                            <i class="fa fa-clone" />
                            Duplicate
                        </button>

                        {#if !patientEncounter.isDeleted}
                            <button
                                class={`${headerRightBtnCls} w-24`}
                                on:click={() => {
                                    goToURL(
                                        `/PatientQuote/edit/${patientEncounter.id}`,
                                    );
                                }}
                            >
                                <i class={`${editIcon}`} />
                                Edit
                            </button>
                        {/if}
                    {/if}   
                {/if}
            </div>
        </div>

        <div class="flex flex-col flex-1 mx-4 mb-4">
            {#if currentTab == "generalInfo"}
                <GeneralInfoTab
                    bind:patientEncounter
                    bind:patientDetails
                    bind:notesRef
                    bind:isNotesInEdit
                    bind:eclipseLogsData
                    {patientContacts}
                    {procedureDetails}
                    {doctorDetails}
                    billingData={patientEncounter.billingData}
                    encounterId={patientEncounter.id}
                    {reloadPatientDetails}
                    {updatePatientEncounter}
                />
            {:else if currentTab == "feeEstimate"}
                <QuoteTab
                    bind:patientEncounterDetails={patientEncounter}
                    {isUpdatingBillingStatus}
                    {billingDetails}
                    {doctorProviders}
                    {latestQuoteData}
                    {latestQuoteDataItems}
                    {quotesData}
                    {reloadBillingDetails}
                />
            {:else if currentTab == "notes"}
                <NotesTab
                    patientID={patientDetails.id}
                    patientHasNotes={patientDetails.hasNotes}
                    encounterID={patientEncounter.id}
                    encounterHasNotes={patientEncounter.hasNotes}
                    {reloadPatientDetails}
                />
            {:else if currentTab == "communication"}
                <CommunicationTab
                    bind:patientEncounterDetails={patientEncounter}
                    bind:isLoading={isCommsLoading}
                    {emailNotifications}
                    {smsNotifications}
                    {patientDetails}
                    {doctorDetails}
                    {reloadCommunication}
                />
            {/if}
        </div>
    </div>
{/if}

<OcrStickerModal
    bind:isModalOpen={isOcrStickerModalOpen}
    encounterId={patientEncounter.id}
    accountId={encounterDoctorId}
/>

{#if convertQuoteToEncounterModalOpen}
    <ConvertQuoteOnlyToPatientEncounter 
        isOpen={convertQuoteToEncounterModalOpen}
        isFromCalendarList={false}
        onClose={_closeConvertToEncounter}
        row={patientEncounter}>
    </ConvertQuoteOnlyToPatientEncounter>
{/if}

<NotificationModal
    isModalOpen={isConfirmUpdateEncounterStatusModalOpen}
    title="Update Fee Estimate Status"
    cancelText="Cancel"
    submitText="Confirm"
    confirmButtonClass={newStatus == DirectEncounterStatus.Archived
        ? "btn-error"
        : "btn-secondary"}
    cancelButtonClass="btn-accent"
    closeModal={_closeConfirmUpdateEncounterStatusModal}
    confirmAction={handleUpdateStatus}
    isConfirming={isUpdatingEncounterStatus}
    closeModalFunction={_closeConfirmUpdateEncounterStatusModal}
>
    {#if encounterHasActiveClaim && checkClaimStatusViaChangeStatus.includes(newStatus)}
        <div class="flex flex-col gap-y-2">
            <p>
                Fee estimate has an active claim. Archiving or cancelling the
                fee estimate will cancel this active claim.
            </p>
            <p>Do you still wish to proceed?</p>
        </div>
    {:else}
        Change fee estimate status to {getEncounterStatus(newStatus)}?
    {/if}
</NotificationModal>
